import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateDataSourceTemplateDocument,
  type ActivateDataSourceTemplateMutation,
  type ActivateDataSourceTemplateMutationVariables,
  DeactivateDataSourceTemplateDocument,
  type DeactivateDataSourceTemplateMutation,
  type DeactivateDataSourceTemplateMutationVariables,
  GetDataSourceTemplateDetailDocument,
  GetDataSourceTemplatesDocument,
} from "~/features/dataSourceTemplates/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface ActivationTarget {
  id: string;
}

/**
 * Activate / deactivate a data-source template. Returns plain submitters and a
 * `processing` flag; the calling detail view owns the confirmation dialog
 * (V1 parity).
 */
export function useDataSourceTemplateActivation() {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const [activateDataSourceTemplate] = useMutation<
    ActivateDataSourceTemplateMutation,
    ActivateDataSourceTemplateMutationVariables
  >(ActivateDataSourceTemplateDocument);
  const [deactivateDataSourceTemplate] = useMutation<
    DeactivateDataSourceTemplateMutation,
    DeactivateDataSourceTemplateMutationVariables
  >(DeactivateDataSourceTemplateDocument);

  const onActivate = useCallback(
    async ({ id }: ActivationTarget) => {
      setProcessing(true);
      try {
        await activateDataSourceTemplate({
          variables: { input: { tenantId, id } },
          refetchQueries: [
            { query: GetDataSourceTemplatesDocument, variables: { tenantId } },
            { query: GetDataSourceTemplateDetailDocument, variables: { tenantId, dataSourceTemplateId: id } },
          ],
        });
        toast.success(t("feature.common.activatedSuccessfully", { model: "dataSourceTemplate" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activateDataSourceTemplate, tenantId, t],
  );

  const onDeactivate = useCallback(
    async ({ id }: ActivationTarget) => {
      setProcessing(true);
      try {
        await deactivateDataSourceTemplate({
          variables: { input: { tenantId, id } },
          refetchQueries: [
            { query: GetDataSourceTemplatesDocument, variables: { tenantId } },
            { query: GetDataSourceTemplateDetailDocument, variables: { tenantId, dataSourceTemplateId: id } },
          ],
        });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "dataSourceTemplate" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivateDataSourceTemplate, tenantId, t],
  );

  return { onActivate, onDeactivate, processing };
}
